import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type PilotSheetRow = {
  catalogId: string;
  actual: string;
  notes: string;
};

export type PilotSheet = {
  id: string;
  createdAt: number;
  batchKg: number;
  rows: Record<string, PilotSheetRow>;
};

export type BatchComment = {
  id: string;
  author: string;
  initials: string;
  ts: number;
  text: string;
};

const sheetKey = (flavorKey: string, variantId: string) =>
  `${flavorKey}::${variantId}`;

// Stable empty references — returning fresh `[]` literals from selector
// helpers makes useSyncExternalStore think the value changed every render,
// which triggers an infinite re-render loop. Cache and reuse instead.
const EMPTY_LOCKED: string[] = [];
const EMPTY_SHEETS: PilotSheet[] = [];
const EMPTY_COMMENTS: BatchComment[] = [];

type BatchStore = {
  locked: Record<string, string[]>;
  sheets: Record<string, PilotSheet[]>;
  comments: Record<string, BatchComment[]>;

  isLocked: (flavorKey: string, variantId: string) => boolean;
  lockedFor: (flavorKey: string) => string[];
  setLocked: (flavorKey: string, variantId: string, locked: boolean) => void;

  getSheets: (flavorKey: string, variantId: string) => PilotSheet[];
  setSheets: (flavorKey: string, variantId: string, sheets: PilotSheet[]) => void;

  getComments: (flavorKey: string) => BatchComment[];
  addComment: (flavorKey: string, comment: BatchComment) => void;
};

export const useBatchStore = create<BatchStore>()(
  persist(
    (set, get) => ({
      locked: {},
      sheets: {},
      comments: {},

      isLocked: (flavorKey, variantId) => {
        const ids = get().locked[flavorKey];
        return !!ids && ids.includes(variantId);
      },
      lockedFor: (flavorKey) => get().locked[flavorKey] || EMPTY_LOCKED,
      setLocked: (flavorKey, variantId, locked) => {
        set((state) => {
          const cur = state.locked[flavorKey] || [];
          if (locked) {
            if (cur.includes(variantId)) return state;
            return {
              locked: { ...state.locked, [flavorKey]: [...cur, variantId] },
            };
          }
          if (!cur.includes(variantId)) return state;
          return {
            locked: {
              ...state.locked,
              [flavorKey]: cur.filter((id) => id !== variantId),
            },
          };
        });
      },

      getSheets: (flavorKey, variantId) =>
        get().sheets[sheetKey(flavorKey, variantId)] || EMPTY_SHEETS,
      setSheets: (flavorKey, variantId, sheets) => {
        set((state) => ({
          sheets: { ...state.sheets, [sheetKey(flavorKey, variantId)]: sheets },
        }));
      },

      getComments: (flavorKey) => get().comments[flavorKey] || EMPTY_COMMENTS,
      addComment: (flavorKey, comment) => {
        set((state) => ({
          comments: {
            ...state.comments,
            [flavorKey]: [...(state.comments[flavorKey] || []), comment],
          },
        }));
      },
    }),
    {
      name: "palate_batch",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        locked: s.locked,
        sheets: s.sheets,
        comments: s.comments,
      }),
    }
  )
);
